import { SetStateAction, Dispatch } from 'react'
import { Message, StreamChunk } from '../types'
import { StreamProcessor } from '../utils/streamProcessor'

interface UseStreamHandlerProps {
  setMessages: Dispatch<SetStateAction<Message[]>>
  setPendingEdit: (message: Message | undefined) => void
  setShowEditConfirmation: (show: boolean) => void
  setWebSearchStatus?: (status: { active: boolean; query?: string; url?: string; domain?: string; title?: string; outputIndex?: number }) => void
  onSearchRequest?: (id: string, query: string, limit: number, fullContent: boolean, assistantMessageId?: string) => Promise<{ results: any[]; summary: string }>
  onSearchComplete?: (searches: Array<{ id: string; query: string; results: any[]; summary: string }>) => void
}

/**
 * Hook for processing streaming responses from the AI API.
 * Handles content accumulation, error responses, edit proposals, and tool calls.
 */
export function useStreamHandler({
  setMessages,
  setPendingEdit,
  setShowEditConfirmation,
  setWebSearchStatus,
  onSearchRequest,
  onSearchComplete
}: UseStreamHandlerProps) {
  // Track searches performed during this stream
  const collectedSearches: Array<{ id: string; query: string; results: any[]; summary: string }> = []
  
  /**
   * Processes a single parsed chunk from the stream.
   * Returns information about whether to continue processing and any tool calls.
   */
  const processStreamChunk = async (
    parsed: StreamChunk | any,
    assistantMessageId: string,
    accumulatedContent: { value: string }
  ): Promise<{ shouldContinue: boolean; toolCall?: any; toolResult?: any }> => {
    // Check if this is a search request from the server
    if (parsed.type === 'searchRequest' && onSearchRequest) {
      console.log('[useStreamHandler] Received search request:', parsed)
      
      try {
        // Execute the search locally and pass the assistantMessageId for tracking
        const searchResults = await onSearchRequest(parsed.id, parsed.query, parsed.limit, parsed.fullContent, assistantMessageId)
        
        // Deduplicate results before adding to collected searches
        // Create a map of existing recordNames across all searches
        const existingRecordNames = new Set(
          collectedSearches.flatMap(s => s.results.map((r: any) => r.recordName))
        )
        
        // Filter out duplicates from new results
        const uniqueResults = searchResults.results.filter((r: any) => !existingRecordNames.has(r.recordName))
        
        // Only add search if it has unique results
        if (uniqueResults.length > 0) {
          collectedSearches.push({
            id: parsed.id,
            query: parsed.query,
            results: uniqueResults,
            summary: `Found ${uniqueResults.length} unique notes matching "${parsed.query}"`
          })
          console.log('[useStreamHandler] Search results collected locally:', parsed.query, '- unique results:', uniqueResults.length)
        } else {
          console.log('[useStreamHandler] No unique results for:', parsed.query, '(all duplicates)')
        }
      } catch (error) {
        console.error('[useStreamHandler] Error executing search:', error)
      }
      
      return { shouldContinue: true }
    }
    
    // Check if this is an error response
    if (parsed.error) {
      console.error('[useStreamHandler] Error from API:', parsed.error)
      setMessages(previous => 
        previous.map(message => 
          message.id === assistantMessageId 
            ? { ...message, content: `Error: ${parsed.error?.message ?? parsed.error}` }
            : message
        )
      )
      return { shouldContinue: false }
    }

    // Handle web search events
    if (parsed.webSearch) {
      console.log('[useStreamHandler] Web search event:', parsed.webSearch)
      
      // Update web search status
      if (setWebSearchStatus) {
        if (parsed.webSearch.status === 'completed') {
          setWebSearchStatus({ active: false })
        } else {
          setWebSearchStatus({
            active: true,
            query: parsed.webSearch.query || undefined,
            url: parsed.webSearch.url || undefined,
            domain: parsed.webSearch.domain || undefined,
            title: parsed.webSearch.title || undefined,
            outputIndex: parsed.webSearch.outputIndex || undefined
          })
        }
      }
      
      return { shouldContinue: true }
    }
    
    // Process normal streaming content
    const content = parsed.choices?.[0]?.delta?.content
    
    if (content !== undefined && content !== null) {
      accumulatedContent.value += content
      
      // Update the assistant message with accumulated content
      setMessages(previous => 
        previous.map(message => 
          message.id === assistantMessageId 
            ? { ...message, content: accumulatedContent.value }
            : message
        )
      )
    }
    
    // Check if this is the final chunk with finish_reason
    const finishReason = parsed.choices?.[0]?.finish_reason
    if (finishReason) {
      console.log('[useStreamHandler] Stream finished with reason:', finishReason)
    }
    
    // Check if the response contains an edit proposal
    if (parsed.proposedEdit) {
      setMessages(previous => 
        previous.map(message => 
          message.id === assistantMessageId 
            ? { ...message, proposedEdit: parsed.proposedEdit }
            : message
        )
      )
    }
    
    // Check if this is a tool call response
    if (parsed.toolCall && parsed.toolResult) {
      return { 
        shouldContinue: true, 
        toolCall: parsed.toolCall, 
        toolResult: parsed.toolResult 
      }
    }
    
    return { shouldContinue: true }
  }

  /**
   * Processes the stream reader and accumulates content.
   * Returns the accumulated content and any tool calls encountered.
   */
  const processStream = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    assistantMessageId: string
  ): Promise<{ 
    accumulatedContent: string; 
    toolCalls: Array<{ toolCall: any; toolResult: any }>;
    searches: Array<{ id: string; query: string; results: any[]; summary: string }>
  }> => {
    const processor = new StreamProcessor()
    const accumulatedContent = { value: '' }
    const toolCalls: Array<{ toolCall: any; toolResult: any }> = []

    console.log('[useStreamHandler] Starting to read stream...')

    while (true) {
      const { value, done } = await reader.read()
      if (done) {
        console.log('[useStreamHandler] Stream complete')
        break
      }

      const lines = processor.processChunk(value)
      
      for (const line of lines) {
        const parsed = processor.parseStreamData(line)
        if (!parsed) continue
        
        const result = await processStreamChunk(parsed, assistantMessageId, accumulatedContent)
        
        if (!result.shouldContinue) {
          return { accumulatedContent: accumulatedContent.value, toolCalls, searches: collectedSearches }
        }
        
        if (result.toolCall && result.toolResult) {
          toolCalls.push({ toolCall: result.toolCall, toolResult: result.toolResult })
        }
      }
    }

    console.log('[useStreamHandler] Final message length:', accumulatedContent.value.length)
    
    // Call onSearchComplete with collected searches
    if (onSearchComplete && collectedSearches.length > 0) {
      console.log('[useStreamHandler] Notifying about', collectedSearches.length, 'searches')
      onSearchComplete(collectedSearches)
    }
    
    return { accumulatedContent: accumulatedContent.value, toolCalls, searches: collectedSearches }
  }

  /**
   * Handles edit proposals found in the accumulated content.
   */
  const handleEditProposal = (
    accumulatedContent: string,
    noteContent: string | null,
    assistantMessageId: string
  ) => {
    const processor = new StreamProcessor()
    const editProposal = processor.extractEditProposal(accumulatedContent)
    
    if (editProposal && noteContent) {
      const cleanContent = processor.cleanContentFromEditProposal(accumulatedContent)
      
      // Update the message with the proposed edit
      setMessages(previous => {
        const updatedMessages = previous.map(message => 
          message.id === assistantMessageId 
            ? { 
                ...message, 
                content: cleanContent,
                proposedEdit: {
                  original: noteContent,
                  modified: editProposal.modified
                }
              }
            : message
        )
        
        // Find the message with the edit and set it as pending
        const editMessage = updatedMessages.find(m => m.id === assistantMessageId)
        if (editMessage?.proposedEdit) {
          setPendingEdit(editMessage)
          setShowEditConfirmation(true)
        }
        
        return updatedMessages
      })
    }
  }

  /**
   * Handles the case when no content is received from the stream.
   */
  const handleEmptyResponse = (assistantMessageId: string) => {
    setMessages(previous => 
      previous.map(message => {
        if (message.id === assistantMessageId) {
          // If we have referenced notes, the AI did search but didn't generate text
          if (message.referencedNotes && message.referencedNotes.length > 0) {
            return { 
              ...message, 
              content: 'I found relevant notes in your knowledge base. The search results are shown below. Please let me know what specific information you\'d like from these notes.' 
            }
          }
          // Otherwise, it's a real error
          return { ...message, content: 'No response received from the AI. Please check the API configuration.' }
        }
        return message
      })
    )
  }

  return { 
    processStream, 
    handleEditProposal,
    handleEmptyResponse
  }
}